// 角色工具类
import envConfig from '@/config/user.env';
import store from '@/store/index';

const _elRole = {
  DISPATCHER: 'dispatcher', // EL-调度员
  DISPATCHER_PHASE2: 'dispatcher_phase2', // EL-调度员二期
  ORDER_CLERK: 'order_clerk', // EL-订单员
  CITY_ORDER_CLERK: 'city_order_clerk', // EL-城配订单员
  TRUNK_ORDER_CLERK: 'trunk_order_clerk', // EL-干线订单工作台
} as const;

/**
 * EL 角色语义枚举（外部一般只 import 本对象 + isMatchedRoleKinds）。
 * 用法示例：EL_ROLE.DISPATCHER、[EL_ROLE.DISPATCHER, EL_ROLE.ORDER_CLERK]
 */
export const EL_ROLE = Object.freeze(_elRole) as Readonly<typeof _elRole>;

export type ElRoleKind = (typeof _elRole)[keyof typeof _elRole];

type EnvKey = 'prod' | 'ver' | 'uat';

type RoleCodesByEnv = Record<EnvKey, Partial<Record<ElRoleKind, string[]>>>;

/** 不同环境下的角色编码（语义枚举值 -> 角色编码数组） */
const ROLE_CODES_BY_ENV: RoleCodesByEnv = {
  prod: {
    [EL_ROLE.DISPATCHER]: ['RL202412160562'],
    [EL_ROLE.DISPATCHER_PHASE2]: ['RL202504160969'],
    [EL_ROLE.ORDER_CLERK]: ['RL202501091051'],
    [EL_ROLE.CITY_ORDER_CLERK]: ['RL202603050712'],
    [EL_ROLE.TRUNK_ORDER_CLERK]: ['RL202603050427'],
  },
  ver: {
    [EL_ROLE.DISPATCHER]: ['RL202412130009'],
    [EL_ROLE.DISPATCHER_PHASE2]: ['RL202504160003'],
    [EL_ROLE.ORDER_CLERK]: ['RL202503240001'],
    [EL_ROLE.CITY_ORDER_CLERK]: ['RL202603110001'],
    [EL_ROLE.TRUNK_ORDER_CLERK]: ['RL202603110002'],
  },
  uat: {
    [EL_ROLE.DISPATCHER]: ['RL202411280002'],
    [EL_ROLE.DISPATCHER_PHASE2]: ['RL202504160001'],
    [EL_ROLE.ORDER_CLERK]: ['RL202503200001'],
    [EL_ROLE.CITY_ORDER_CLERK]: ['RL202603050002'],
    [EL_ROLE.TRUNK_ORDER_CLERK]: ['RL202603050001'],
  },
};

function getEnvKey(): EnvKey {
  const env = (envConfig.env || 'UAT').toString().toUpperCase();
  if (env === 'PROD') return 'prod';
  if (env === 'VER') return 'ver';
  return 'uat';
}

/** 从 curRole / 角色字典中解析当前生效的角色编码（支持逗号分隔，取首个） */
function parseCurrentRoleCode(raw?: string | null): string {
  if (typeof raw !== 'string') return '';
  const code = raw.trim();
  if (!code) return '';
  return code.split(',')[0].trim();
}

/**
 * 外部只传「允许的语义枚举数组」（元素为 EL_ROLE.xxx 对应的字符串），
 * 内部按当前环境展开为角色编码后，判断当前角色编码是否命中其中任意一个。
 */
export function isMatchedRoleKinds(allowedRoleKinds: readonly ElRoleKind[] = []): boolean {
  if (!Array.isArray(allowedRoleKinds) || allowedRoleKinds.length === 0) return false;
  // 基座 vuex 使用 curRole；currentRoleDic 仅存在于子应用 micro 容器本地 state
  const currentRoleCode = parseCurrentRoleCode(store?.state?.curRole);
  if (!currentRoleCode) return false;

  const roleCodeMap: Partial<Record<ElRoleKind, string[]>> =
    ROLE_CODES_BY_ENV[getEnvKey()] ?? {};
  const allowedRoleCodes = [
    ...new Set(
      allowedRoleKinds.flatMap((kind: ElRoleKind) => {
        const codes = roleCodeMap[kind];
        return Array.isArray(codes) ? codes.map((item) => item.trim()).filter(Boolean) : [];
      }),
    ),
  ];

  if (allowedRoleCodes.length === 0) return false;
  return allowedRoleCodes.includes(currentRoleCode);
}
